import Dialog from "@/molecules/dialog";
import { styles } from "./style";
import {
  Box,
  Button,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Checkbox,
  TableContainer,
} from "@/common/styles/style";
import DataInput from "@/atoms/dataInput";
import { useCallback, useEffect, useState } from "react";
import { apiMachineType } from "@/common/type/type";
import useCheckbox from "@/hooks/useCheckbox";
import {
  convertToTrendFormat,
  formatDateTime,
  lastWeek,
  today,
} from "@/helpers/getUtcOffset";
import {
  CollectionResultType,
  CollectionType,
} from "@/pages/collectionDataSet/type";

interface CSVDownloadDialogProps {
  open: boolean;
  machine: apiMachineType;
  collectedData: CollectionType[];
  onClose: () => void;
}

export default function CSVDownloadDialog({
  open,
  machine,
  collectedData,
  onClose,
}: CSVDownloadDialogProps) {
  const [startDate, setStartDate] = useState<string>(formatDateTime(lastWeek));
  const [endDate, setEndDate] = useState<string>(formatDateTime(today));
  const {
    checkedItems,
    handleCheckboxChange,
    handleParentCheckboxChange,
    resetCheckboxes,
  } = useCheckbox(collectedData.length);

  useEffect(() => {
    if (!open) {
      resetCheckboxes();
    }
  }, [open]);

  const handleDateChange = useCallback(
    (type: string) => (value: string) => {
      const dateObj = new Date(value);
      if (type === "start") {
        setStartDate(formatDateTime(dateObj));
      } else if (type === "end") {
        setEndDate(formatDateTime(dateObj));
      }
    },
    []
  );

  const handleDownload = async () => {
    if (!collectedData) {
      alert("データが存在しません。");
      return;
    }

    const selectedDataList = collectedData.filter(
      (_, index) => checkedItems[index]
    );

    if (selectedDataList.length === 0) {
      alert("選択されたデータがありません。");
      return;
    }

    const startAt = convertToTrendFormat(startDate);
    const endAt = convertToTrendFormat(endDate);
    let resultData: CollectionResultType[] = [];

    try {
      await Promise.all(
        selectedDataList.map(async (dataList) => {
          const machineDataQuery = `machineId=${machine.id}&dataName=${dataList.dataName}`;
          const dateQuery = `&startAt=${startAt}&endAt=${endAt}`;
          const response = await fetch(`/api/dashboard/widget/trend/collectedData?${machineDataQuery}${dateQuery}`, {
              method: "GET",
              credentials: "include",
          });

          const data = await response.json();
          if (response.ok) {
            console.log(data.message);
            resultData = [
              ...resultData,
              ...data.result.map((entry: CollectionResultType) => {
                const { machineId, ...rest } = entry;
                return rest;
              }),
            ];
          } else {
            console.error(
              `${dataList.dataName} サーバ接続エラー: `,
              response.statusText
            );
          }
        })
      );
    } catch (error) {
      console.error("サーバ接続エラー:", error);
    }

    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    const csvRows: string[][] = [];
    csvRows.push(["機器番号", machine.machineNumber]);

    resultData.forEach((entry, index) => {
      for (const [dataName, dataList] of Object.entries(entry)) {
        if (Array.isArray(dataList)) {
          csvRows.push(["名称", dataName]);

          const filteredData = dataList.filter((entry) => {
            const entryTimestamp = new Date(entry.timestamp);
            return (
              entryTimestamp >= startDateObj && entryTimestamp <= endDateObj
            );
          });

          const dateRow: string[] = ["日付"];
          const timeRow: string[] = ["時間"];
          const valueRow: string[] = ["値"];
          const alarmRow: string[] = ["アラーム"];

          const sortedData = filteredData.sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );

          sortedData.forEach((entry) => {
            const entryDate = new Date(entry.timestamp);
            const alarm = entry.hasAlert ? "✓" : "";

            const year = String(entryDate.getFullYear()).slice(2);
            const month = entryDate.getMonth() + 1;
            const day = entryDate.getDate();
            const formattedDate = `="${year}/${month}/${day}"`;

            const formattedTime = entryDate
              .toISOString()
              .split("T")[1]
              .split(".")[0];

            dateRow.push(formattedDate);
            timeRow.push(formattedTime);
            valueRow.push(String(entry.value));
            alarmRow.push(alarm);
          });

          csvRows.push(dateRow, timeRow, valueRow, alarmRow);

          // 最後の項目でなければ空いている一行を追加
          if (index !== resultData.length - 1) {
            csvRows.push([]);
          }
        }
      }
    });

    // CSV ファイル生成
    const csvContent =
      "data:text/csv;charset=utf-8,\uFEFF" +
      csvRows.map((row) => row.join(",")).join("\n");

    const now = new Date();
    const japanTime = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    const formattedDate = japanTime
      .toISOString()
      .slice(0, 16)
      .replace(/[-:T]/g, "");
    const fileName = `${machine.machineNumber}-${formattedDate}.csv`;

    // CSV ダウンロード
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const dataInputField = [
    { label: "From", value: startDate, onchangeKey: "start" },
    { label: "To", value: endDate, onchangeKey: "end" },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="CSVダウンロード"
      sx={styles.container}
      contentChildren={
        <Box>
          <Typography variant="h6" sx={styles.title}>
            期間
          </Typography>
          <Stack direction="row" spacing={2}>
            {dataInputField.map((dataField) => (
              <DataInput
                datatype="TextField"
                label={dataField.label}
                textProps={{
                  type: "datetime-local",
                  value: dataField.value,
                  sx: styles.dataInput,
                }}
                onChange={handleDateChange(dataField.onchangeKey)}
                key={dataField.label}
              />
            ))}
          </Stack>
          <Typography variant="h6" sx={styles.title}>
            対象データ
          </Typography>
          <Box>
            <Table sx={styles.table}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={styles.tableCell}>
                    <Checkbox
                      checked={
                        checkedItems.length > 0 &&
                        checkedItems.every((item) => item)
                      }
                      indeterminate={
                        checkedItems.some((item) => item) &&
                        !checkedItems.every((item) => item)
                      }
                      onChange={handleParentCheckboxChange}
                    />
                  </TableCell>
                  <TableCell align="center" sx={styles.tableCell}>
                    <Typography variant="h6">名前</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
            <TableContainer sx={styles.tableContainer}>
              <Table>
                <TableBody>
                  {collectedData &&
                    collectedData.map((label, index) => (
                      <TableRow key={label.dataName}>
                        <TableCell align="center" sx={styles.tableCell}>
                          <Checkbox
                            checked={checkedItems[index] || false}
                            onChange={(e) => handleCheckboxChange(index, e)}
                          />
                        </TableCell>
                        <TableCell align="center" sx={styles.tableCell}>
                          <Typography variant="body1">
                            {label.dataName}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      }
      actionsChildren={
        <Stack direction="row" spacing={2} sx={styles.buttonArea}>
          <Button variant="outlined" onClick={onClose}>
            <Typography variant="button">キャンセル</Typography>
          </Button>
          <Button variant="contained" onClick={handleDownload}>
            <Typography variant="button">ダウンロード</Typography>
          </Button>
        </Stack>
      }
    />
  );
}
